import { useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Container
} from '@mui/material'
import CertificateWizard from './CertificateWizard'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'

interface AdminSetupFlowProps {
  tenantId: string
  tenantDomain: string
}

/**
 * AdminSetupFlow component
 * 
 * This component is shown when a customer is created manually by an admin
 * (not through Azure Marketplace). It guides the customer through the
 * certificate wizard to set up app registration, certificate, and admin consent.
 * 
 * The admin portal generates a hash of "AdminSetup:{tenantId}" and stores it
 * in the MarketplaceSubscriptionStatus field. When this component detects
 * that hash, it shows the certificate wizard.
 * 
 * After the wizard completes, this component clears the hash so the customer
 * can access the normal dashboard.
 */
export default function AdminSetupFlow({ tenantId, tenantDomain }: AdminSetupFlowProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  const handleWizardComplete = async () => {
    try {
      setLoading(true)
      setError(null)

      // Clear the AdminSetup hash by setting MarketplaceSubscriptionStatus to 'Completed'
      await api.put(`/tenants/${tenantId}/subscription`, {
        marketplaceSubscriptionStatus: 'Completed'
      })

      // Navigate to main policies dashboard
      navigate('/')
      
      // Reload the page to refresh customer data
      window.location.reload()
    } catch (err: any) {
      console.error('Failed to complete admin setup:', err)
      setError(err.response?.data?.error || 'Failed to complete setup. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            gap: 2
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="body1" color="text.secondary">
            Completing setup...
          </Typography>
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" gutterBottom>
              Welcome! Let's Set Up Your Configuration
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Before you can start deploying policies, we need to configure authentication
              between your Microsoft 365 tenant and our platform.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              This one-time setup wizard will guide you through:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>Signing in to your Microsoft 365 tenant as an admin</li>
              <li>Generating a security certificate</li>
              <li>Creating an app registration in your tenant</li>
              <li>Granting the necessary permissions</li>
              <li>Uploading the certificate securely</li>
            </Box>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              This process typically takes 2 to3 minutes.
            </Typography>
          </Box>

          <CertificateWizard
            tenantId={tenantId}
            tenantDomain={tenantDomain}
            onComplete={handleWizardComplete}
          />
        </Paper>
      </Box>
    </Container>
  )
}
